<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';
import { Capacitor } from '@capacitor/core';
import AppSheet from '@/components/common/AppSheet.vue';
import AppContent from '@/components/common/AppContent.vue';
import AppModalHeader from '@/components/common/AppModalHeader.vue';
import Spinner from '@/components/common/Spinner.vue';
import { Button } from '@/components/ui/button';
import ChoicePillGroup, { type PillOption } from '@/components/tasks/form/ChoicePillGroup.vue';
import { useFeedbackStore } from '@/stores/feedback';
import { useAuthStore } from '@/stores/auth';
import { useAnalytics } from '@/composables/useAnalytics';
import { CONTACT_TOPICS, feedbackService, type ContactTopic } from '@/services/feedback.service';
import { getAppVersion } from '@/utils/appVersion';

/**
 * « Nous contacter » (HARMONIA-54). Remplace le formulaire Teamwork ouvert dans
 * un navigateur : le parent reste dans l'app, et le message part par la même
 * fonction que l'avis (send-feedback-email-mailtrap), sans les quatre notes.
 * La réponse vise son adresse : « Répondre » depuis la boîte support suffit.
 */
const MESSAGE_MAX = 1000;

const store = useFeedbackStore();
const authStore = useAuthStore();
const { t, locale } = useI18n({ useScope: 'global' });
const { logEvent } = useAnalytics();

const isOpen = computed(() => store.isOpen && store.mode === 'contact');
const topic = ref<ContactTopic>('question');
const message = ref('');
const submitting = ref(false);

const email = computed(() => authStore.user?.email ?? '');
const canSubmit = computed(() => message.value.trim().length > 0 && !submitting.value);

const topicOptions = computed<PillOption[]>(() =>
  CONTACT_TOPICS.map((key) => ({
    value: key,
    label: t(`contact.topics.${key}`),
    testid: `contact-topic-${key}`,
  })),
);

watch(isOpen, (open) => {
  if (!open) return;
  topic.value = store.topic ?? 'question';
  message.value = '';
  submitting.value = false;
  logEvent('contact_form_opened', { source: store.source ?? 'unknown', topic: topic.value });
});

function onTopic(value: string | number | null) {
  // Pas de désélection : un message part toujours avec un sujet.
  if (value !== null) topic.value = value as ContactTopic;
}

function detectPlatform(): 'ios' | 'android' | 'web' {
  if (!Capacitor.isNativePlatform()) return 'web';
  return Capacitor.getPlatform() === 'android' ? 'android' : 'ios';
}

function close() {
  if (submitting.value) return;
  store.close();
}

async function submit() {
  if (!canSubmit.value) return;
  submitting.value = true;
  const source = store.source ?? 'unknown';
  const result = await feedbackService.submitContactMessage({
    kind: 'contact',
    topic: topic.value,
    email: email.value,
    message: message.value.trim(),
    app_version: await getAppVersion(),
    platform: detectPlatform(),
    locale: locale.value,
  });
  submitting.value = false;

  if (result.ok) {
    logEvent('contact_form_submitted', { source, topic: topic.value });
    toast.success(t('contact.toast.sent'), { duration: 2500 });
    store.close();
    return;
  }
  logEvent('contact_form_submit_failed', { source, topic: topic.value, error_code: result.error ?? 'unknown' });
  toast.error(t(result.error === 'rate_limit' ? 'contact.toast.rateLimited' : 'contact.toast.error'), {
    duration: 3500,
  });
}
</script>

<template>
  <AppSheet
    mode="sheet"
    :is-open="isOpen"
    :breakpoints="[0, 1]"
    :initial-breakpoint="1"
    @update:is-open="(v: boolean) => { if (!v) close() }"
  >
    <AppContent>
      <AppModalHeader
        :title="t('contact.title')"
        :subtitle="t('contact.subtitle')"
        icon="message-circle"
        show-close
        :close-label="t('common.close')"
        @close="close"
      />

      <form class="px-5 pb-6 flex flex-col gap-4" data-testid="contact-form" @submit.prevent="submit">
        <section class="bg-white rounded-2xl border border-line p-4 space-y-3">
          <p class="text-[13px] font-semibold text-ink-2">{{ t('contact.topicLabel') }}</p>
          <ChoicePillGroup :options="topicOptions" :model-value="topic" @update:model-value="onTopic" />
        </section>

        <section class="bg-white rounded-2xl border border-line p-4 space-y-2">
          <div class="form-group">
            <label for="contact-message">{{ t('contact.messageLabel') }}</label>
            <textarea
              id="contact-message"
              v-model="message"
              data-testid="contact-message"
              rows="6"
              :maxlength="MESSAGE_MAX"
              :placeholder="t('contact.placeholder')"
            ></textarea>
          </div>
          <p class="text-right text-[11px] text-ink-3 tabular-nums">{{ message.length }} / {{ MESSAGE_MAX }}</p>
        </section>

        <p v-if="email" class="text-[13px] text-ink-3 px-1" data-testid="contact-reply-to">
          {{ t('contact.replyTo', { email }) }}
        </p>
      </form>
    </AppContent>

    <div class="shrink-0 border-t border-line bg-[var(--card)] pb-[env(safe-area-inset-bottom)]">
      <div class="flex gap-2 p-4">
        <Button expand="block" color-variant="outline" class="flex-1" :disabled="submitting" @click="close">
          {{ t('common.cancel') }}
        </Button>
        <Button
          data-testid="contact-submit"
          expand="block"
          color="primary"
          class="flex-1"
          :disabled="!canSubmit"
          @click="submit"
        >
          <template v-if="submitting" #start>
            <Spinner :size="18" />
          </template>
          {{ t('contact.submit') }}
        </Button>
      </div>
    </div>
  </AppSheet>
</template>
